import { useEffect } from "react"

import { getEnvironment } from "@/config/env"

/**
 * Hook that automatically switches favicons based on:
 * 1. Current path (admin panel gets dedicated admin favicon)
 * 2. Current environment (local/staging/production)
 * 3. Dark mode preference
 *
 * Favicon directory structure:
 * - /public/              - Production light mode
 * - /public/darkmode/     - Production dark mode
 * - /public/local-dark/   - True localhost dev (always dark)
 * - /public/dev-dark/     - Porter dev environment (always dark)
 * - /public/staging-dark/ - Staging (always dark)
 * - /public/admin-dark/   - Admin panel (always, all environments)
 *
 * Local and staging environments always use dark variants for easy visual distinction.
 * Admin panel always uses admin-dark favicon regardless of environment.
 */
export const useFaviconSwitcher = (pathname?: string) => {
  useEffect(() => {
    const env = getEnvironment()

    const updateFavicons = (isDark: boolean) => {
      let faviconPrefix = ""

      // Check if we're on the admin panel (use pathname prop or fallback to window.location)
      const currentPath = pathname || window.location.pathname
      const isAdminPanel = currentPath.startsWith("/admin")

      // Admin panel always gets its own dedicated favicon
      if (isAdminPanel) {
        faviconPrefix = "/admin-dark"
      } else {
        // Environment-specific favicon logic for non-admin pages
        switch (env) {
          case "local":
            // Distinguish between true localhost and Porter dev
            if (typeof window !== "undefined" && window.location.hostname === "localhost") {
              faviconPrefix = "/local-dark" // True localhost
            } else {
              faviconPrefix = "/dev-dark" // Porter dev environment
            }
            break
          case "staging":
            // Always use staging-dark in staging
            faviconPrefix = "/staging-dark"
            break
          case "production":
            // Production respects user's dark mode preference
            faviconPrefix = isDark ? "/darkmode" : ""
            break
        }
      }

      const faviconIco = document.getElementById("favicon-ico") as HTMLLinkElement
      const favicon16 = document.getElementById("favicon-16") as HTMLLinkElement
      const favicon32 = document.getElementById("favicon-32") as HTMLLinkElement
      const faviconApple = document.getElementById("favicon-apple") as HTMLLinkElement

      if (faviconIco) faviconIco.href = `${faviconPrefix}/favicon.ico`
      if (favicon16) favicon16.href = `${faviconPrefix}/favicon-16x16.png`
      if (favicon32) favicon32.href = `${faviconPrefix}/favicon-32x32.png`
      if (faviconApple) faviconApple.href = `${faviconPrefix}/apple-touch-icon.png`
    }

    // Set initial favicon based on current dark mode state (or force dark in dev)
    const htmlElement = document.documentElement
    const isDark = htmlElement.classList.contains("dark")
    updateFavicons(isDark)

    // Watch for dark mode changes using MutationObserver
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const isDark = htmlElement.classList.contains("dark")
          updateFavicons(isDark)
        }
      }
    })

    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ["class"]
    })

    return () => {
      observer.disconnect()
    }
  }, [pathname])
}
